import React from 'react';
import { observer } from 'mobx-react';
import Hls from 'hls.js';

import { isMobile } from 'react-device-detect';
import * as Sentry from '@sentry/browser';

import Alert from '../../generalComponents/Alert';
import { hlsErrorIds } from '../../../constants/hlsErrorsIds';
import { status } from '../../../constants/sfwStatus';
import GroupShowStore from '../../../store/GroupShowStore';
import { isSubscribedToGroupShowAnchor } from '../../../utils/helpers';

import GroupShow from './GroupShow/GroupShow';
import GroupShowOverlay from './GroupShowOverlay/GroupShowOverlay';
import GroupShowNotAvailableOverlay from './GroupShowNotAvailableOverlay/GroupShowNotAvailableOverlay';
import TulumStreamNotAvailableOverlay from './TulumStreamNotAvailableOverlay/TulumStreamNotAvailableOverlay';

import './css/AnchorLiveFeedPlayer.css';
import './css/AnchorLiveFeedPlayer.mobile.css';
import './css/safari-fullscreen-fix.css';
import LiveFeedCTAMessage from './LiveFeedCTAMessage';

import StreamPlayer from './StreamPlayer';


@observer
class AnchorLiveFeedPlayer extends React.Component {
  hls = {};
  videoRef = React.createRef();
  videoWrapRef = React.createRef();
  loadSourceRetries = 5;

  state = {
    muted: false,
    volume: 1,
    showPlay: false,
    loading: true,
    streamErrorMessage: '',
    orientation: 'portrait',
    isAlertVisible: false,
    alertContent: '',
  };

  loadHLS = (hls, url, videoRef) => {
    hls.loadSource(url);
    hls.attachMedia(videoRef);
  };

  componentDidMount() {
    Sentry.configureScope((scope) => {
      scope.setTag('NN-Page', 'anchor');
      scope.setTag('NN-Page Anchor ID', this.props.anchorId);
      scope.setTag('NN-Page Anchor Username', this.props.anchorUsername);
    });
  }

  componentWillReceiveProps(newProps) {
    if (newProps.broadcasterClosedSession) {
      this.setState({ streamErrorMessage: 'The anchor has ended the broadcasting session' });
      setTimeout(() => {
        this.props.uiStore.notify({
          content: {
            type: 'anchorStoppedBroadcast',
            anchorId: this.props.anchorId,
            anchorName: this.props.title,
            anchorUserName: this.props.anchorUsername,
          },
        });
        this.props.onLiveSessionEnd();
        this.closeGroupShow();
      }, 6000);
    }
    if (newProps.spotlighterClosedSession) {
      this.setState({ streamErrorMessage: 'The spotlighting session has ended' });
      setTimeout(() => {
        this.props.uiStore.notify({
          content: {
            type: 'anchorStoppedBroadcast',
            anchorId: this.props.anchorId,
            anchorName: this.props.title,
            anchorUserName: this.props.anchorUsername,
          },
        });
        this.closeGroupShow();
        this.props.onLiveSessionEnd();
      }, 6000);
    }
  }

  closeGroupShow = () => {
    if (GroupShowStore.isInGroupShow) {
      setTimeout(() => {
        GroupShowStore.setInGroupShow(false);
        GroupShowStore.resetStreamVolume();
      }, 5000);
    }
  };

  handleHLSError = (error) => {
    const { broadcasterClosedSession, spotlighterClosedSession, onTulumStreamError } = this.props;
    const { type, details, fatal } = error;
    const errorId = hlsErrorIds[details];

    // Display only fatal hls errors during the broadcast
    const showError = fatal && errorId && !broadcasterClosedSession && !spotlighterClosedSession;
    if (showError) {
      this.displayAlert(type, errorId);
    }

    if (onTulumStreamError) {
      onTulumStreamError(error);
    }
  };

  startBroadcast = () => {
    const { anchorVideoUrl } = this.props;
    !isMobile && this.customVolume(1);
    const video = document.getElementById('video');
    if (Hls.isSupported()) {
      this.hls = new Hls();
      this.loadHLS(this.hls, anchorVideoUrl.streamViews[1].url, video);
      this.hls.on(Hls.Events.ERROR, (_, error) => {


        if (this.loadSourceRetries) {
          this.loadSourceRetries && this.props.onNetworkError();
          this.loadSourceRetries--;
        }
      });
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.loadSourceRetries = 5;
        video.
          play().
          then(() => {
            this.setState({
              showPlay: false,
              loading: false,
            });
          }).
          catch(() => {
            this.setState({
              showPlay: true,
              loading: false,
            });
          });
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = anchorVideoUrl;
      this.setState({
        showPlay: true,
        loading: false,
      });
    }
  };

  displayAlert = (errorType, errorId) => {
    this.setState({
      isAlertVisible: true,
      alertContent: `Error type: ${errorType}. Error ID: ${errorId}`,
    });
  };

  toggleFullscreen = () => {
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (this.videoWrapRef.current.requestFullscreen) {
        this.videoWrapRef.current.requestFullscreen();
      } else if (this.videoWrapRef.current.msRequestFullscreen) {
        this.videoWrapRef.current.msRequestFullscreen();
      } else if (this.videoWrapRef.current.mozRequestFullScreen) {
        this.videoWrapRef.current.mozRequestFullScreen();
      } else if (this.videoWrapRef.current.webkitRequestFullscreen) {
        this.videoWrapRef.current.webkitRequestFullscreen(
          Element.ALLOW_KEYBOARD_INPUT
        );
      }
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  };

  clearLoadRetries = () => {
    this.loadSourceRetries = null;
  }


  hideAlert = () => {
    this.setState({
      isAlertVisible: false,
      alertContent: '',
    });
  };

  render() {
    const {
      anchorId,
      safeForWatchStatus,
      canSubscribeToAnchor,
      isAnchor,
      broadcasterClosedSession,
      anchorVideoUrl,
      isAlreadySubscribedToAnchor,
      nsfw,
      user,
      anchorUsername,
      slug,
      anchorName,
      setTippingModal,
      isBanned,
      spotlighterClosedSession,
      isLivePage,
      hasStreamViews,
      isSpotlight,
      subscribedToOneOfTheAnchors,
      toggleStreamLoading,
      shouldHideVolumeRange,
      isTulumRealityShow,
      onAllowStreamChangeOnError,
      shouldDisplayPlayer,
      onLiveSessionEnd,
    } = this.props;
    const {
      streamErrorMessage,
      orientation,
      isAlertVisible,
      alertContent,
    } = this.state;

    const isSubscribed = (isAlreadySubscribedToAnchor && isAlreadySubscribedToAnchor.active) || (isAlreadySubscribedToAnchor && isLivePage);
    const canWatchSpotlight = isSpotlight && subscribedToOneOfTheAnchors;

    const shouldStart = (isSubscribed || canWatchSpotlight || isAnchor) ? true : safeForWatchStatus === status.SFW;

    let shouldShowSubscriveOverlay = (isSubscribed || canWatchSpotlight) ? (nsfw && !isAnchor) : safeForWatchStatus !== status.SFW && !isAnchor;

    if (isSpotlight) {
      shouldShowSubscriveOverlay = !subscribedToOneOfTheAnchors && nsfw;
    }

    const {
      isInGroupShow,
      transitionToGroupShow,
      groupShowParticipant: { anchorDetails },
    } = GroupShowStore;
    const groupShowHost = {
      anchorName,
      anchorUsername,
      anchorId,
    };
    let isSubscribedToGroupShowHost = false;
    let isSubscribedToGroupShowParticipant = false;
    const groupShowFeatureAvailable = user ? user.features.includes('TEMPTED_PRIVATE_SHOW') : false;

    if (isInGroupShow) {
      isSubscribedToGroupShowHost = isSubscribedToGroupShowAnchor(user, anchorId);
      isSubscribedToGroupShowParticipant = isSubscribedToGroupShowAnchor(user, anchorDetails.id);
    }

    const isSubscribedToOneGroupShowAnchor = isSubscribedToGroupShowHost || isSubscribedToGroupShowParticipant;

    const showLiveShowOverlay = (shouldShowSubscriveOverlay && !isInGroupShow && !isSpotlight) || isBanned;
    const showGroupShowOverlay = isInGroupShow && groupShowFeatureAvailable && (!isSubscribedToOneGroupShowAnchor || isBanned);
    const showSpotlightOverlay = isSpotlight && ((nsfw && !subscribedToOneOfTheAnchors) || isBanned);

    return (
      <div ref={this.videoWrapRef} className="anchor-live-feed-player">
        <LiveFeedCTAMessage
          anchorId={anchorId}
          canSubscribeToAnchor={canSubscribeToAnchor}
          isBanned={isBanned}
          isTulumRealityShow={isTulumRealityShow}
          showGroupShowOverlay={showGroupShowOverlay}
          showLiveShowOverlay={showLiveShowOverlay}
          showSpotlightOverlay={showSpotlightOverlay}
          broadcasterClosedSession={broadcasterClosedSession}
          spotlighterClosedSession={spotlighterClosedSession}
          slug={slug}
          user={user}
        />
        {isInGroupShow && groupShowFeatureAvailable && (
          <GroupShow
            groupShowHost={groupShowHost}
            isSubscribed={isSubscribedToOneGroupShowAnchor}
            setTippingModal={setTippingModal}
            user={user}
            onHLSError={this.handleHLSError}
            onLiveSessionEnd={this.props.onLiveSessionEnd}
          />
        )}
        {isInGroupShow && !groupShowFeatureAvailable && (
          <GroupShowNotAvailableOverlay
            anchorName={anchorName}
          />
        )}
        {transitionToGroupShow && (
          <GroupShowOverlay />
        )}
        {!shouldDisplayPlayer && (
          <TulumStreamNotAvailableOverlay />
        )}
        <StreamPlayer
          anchorUsername={anchorUsername}
          shouldStart={shouldStart}
          broadcasterClosedSession={broadcasterClosedSession}
          shouldHideVolumeRange={shouldHideVolumeRange}
          spotlighterClosedSession={spotlighterClosedSession}
          hasStreamViews={hasStreamViews}
          isTulumRealityShow={isTulumRealityShow}
          anchorVideoUrl={anchorVideoUrl}
          toggleStreamLoading={toggleStreamLoading}
          user={user}
          orientation={orientation}
          onAllowStreamChangeOnError={onAllowStreamChangeOnError}
          streamErrorMessage={streamErrorMessage}
          shouldShowSubscriveOverlay={shouldShowSubscriveOverlay}
          isInGroupShow={isInGroupShow}
          isBanned={isBanned}
          shouldDisplayPlayer={shouldDisplayPlayer}
          toggleFullscreen={this.toggleFullscreen}
          onLiveSessionEnd={onLiveSessionEnd}
          handleHLSError={this.handleHLSError}
          clearLoadRetries={this.clearLoadRetries}
        />
        {isAlertVisible && (
          <Alert onClose={this.hideAlert}>
            {alertContent}
            <p>Please try refreshing the page.</p>
          </Alert>
        )}
      </div>
    );
  }
}

export default AnchorLiveFeedPlayer;
